import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { PaymentForm } from 'payload-react'

const visuallyHidden = {
  position: 'absolute' as const,
  opacity: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
}

function cameraErrorMessage(err: any) {
  if (err?.name === 'NotAllowedError') return 'Camera access was blocked. Allow camera access in your browser settings and try again.'
  if (err?.name === 'NotFoundError') return 'No camera was found on this device.'
  if (err?.name === 'NotReadableError') return 'The camera is in use by another application.'
  return 'Unable to start the camera.'
}

// Live webcam preview for desktop browsers, which ignore the input's capture attribute
function CameraCaptureDialog({ open, fileName, onClose, onCapture }: {
  open: boolean,
  fileName: string,
  onClose: () => void,
  onCapture: (file: File) => void,
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [stream, setStream] = useState<MediaStream>()
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (!open) return
    let activeStream: MediaStream | undefined
    let cancelled = false
    setError(undefined)
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        activeStream = s
        setStream(s)
      })
      .catch((err) => setError(cameraErrorMessage(err)))
    return () => {
      cancelled = true
      activeStream?.getTracks().forEach((t) => t.stop())
      setStream(undefined)
    }
  }, [open])

  // The dialog mounts its content asynchronously, so attach the stream whenever the video element appears
  const attachVideo = (el: HTMLVideoElement | null) => {
    videoRef.current = el
    if (el && stream && el.srcObject !== stream) el.srcObject = stream
  }

  const capture = () => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    canvas.getContext('2d')?.drawImage(video, 0, 0)
    canvas.toBlob((blob) => {
      if (!blob) return
      onCapture(new File([blob], fileName, { type: 'image/jpeg' }))
      onClose()
    }, 'image/jpeg', 0.92)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Take Photo</DialogTitle>
      <DialogContent>
        {error
          ? <Alert severity="error">{error}</Alert>
          : <video ref={attachVideo} autoPlay playsInline muted style={{ width: '100%', borderRadius: 4, background: '#000' }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={capture} disabled={!stream}>Capture</Button>
      </DialogActions>
    </Dialog>
  )
}

function CheckImageInput({ label, name, photoFileName }: { label: string, name: string, photoFileName: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string>()
  const [cameraOpen, setCameraOpen] = useState(false)

  const openFilePicker = (useNativeCamera: boolean) => {
    const input = inputRef.current
    if (!input) return
    // On phones, capture opens the rear camera directly; PDFs in accept can make some Android browsers show a picker instead
    if (useNativeCamera) {
      input.setAttribute('capture', 'environment')
      input.setAttribute('accept', 'image/*')
    } else {
      input.removeAttribute('capture')
      input.setAttribute('accept', 'image/*,.pdf')
    }
    input.click()
  }

  const takePhoto = () => {
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches
    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) openFilePicker(true)
    else setCameraOpen(true)
  }

  // Put the webcam photo into the real input so PaymentForm submits it like a chosen file
  const handleCapture = (file: File) => {
    const input = inputRef.current
    if (!input) return
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    input.files = dataTransfer.files
    setFileName(file.name)
  }

  return (
    <Box position="relative">
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" onClick={takePhoto}>Take Photo</Button>
        <Button variant="outlined" onClick={() => openFilePicker(false)}>Choose File</Button>
        {fileName && <Typography variant="body2">{fileName}</Typography>}
      </Box>
      {/* Visually hidden rather than display:none so required validation still works */}
      <input
        ref={inputRef}
        type="file"
        pl-input={name}
        accept="image/*,.pdf"
        required
        onChange={(e) => setFileName(e.target.files?.[0]?.name)}
        style={visuallyHidden}
      />
      <CameraCaptureDialog
        open={cameraOpen}
        fileName={photoFileName}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCapture}
      />
    </Box>
  )
}

export function EarnestMoneyTaskPayloadCheckForm({ clientToken, onProcessed, onError, onCancel }: {
  clientToken: string,
  onProcessed?: (event: any) => void,
  onError?: (event: any) => void,
  onCancel?: () => void,
}) {
  // Payload doesn't emit processing until after its own submit handling, so track the native submit to show a spinner immediately
  const [submitting, setSubmitting] = useState(false)

  const handleProcessed = (event: any) => {
    console.log('Check payment processed:', event.transaction_id)
    onProcessed?.(event)
  }

  const handleError = (event: any) => {
    setSubmitting(false)
    console.error('Check payment error:', event)
    onError?.(event)
  }

  return (
    <Box onSubmit={() => setSubmitting(true)}>
      <PaymentForm
        clientToken={clientToken}
        preventDefaultOnSubmit
        // onProcessed={handleProcessed}
        onError={handleError}
        onDeclined={handleError}
        onSuccess={handleProcessed}
        onInvalid={() => setSubmitting(false)}>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>

          <label>Account Holder Name</label>
          <input type="text" pl-input="account_holder" required />
          <CheckImageInput label="Check Front Image" name="check_front[file]" photoFileName="check-front.jpg" />
          <CheckImageInput label="Check Back Image" name="check_back[file]" photoFileName="check-back.jpg" />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            {onCancel && <Button onClick={onCancel} disabled={submitting}>Cancel</Button>}
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? <CircularProgress size={20} /> : 'Submit Check'}
            </Button>
          </Box>
        </Box>
      </PaymentForm>
    </Box>
  )
}
